import React from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { Version } from '../services/versionService';
import { VersionTypeBadge } from './changelog/VersionTypeBadge';
import { VersionAreaBadge } from './changelog/VersionAreaBadge';
import { RupturaBadge } from './changelog/RupturaBadge';
import { formatDateOnly } from '../utils/formatters';

interface VersionTableRowProps {
  readonly version: Version;
  readonly isSelected: boolean;
  readonly onSelect: (id: string) => void;
  readonly onEdit: (version: Version) => void;
  readonly onDelete: (version: Version) => void;
  /** true en la primera fila del grupo: renderiza la celda de versión con rowSpan */
  readonly showVersionCell?: boolean;
  /** rowSpan para la celda de versión (igual al número de acciones del grupo) */
  readonly versionRowSpan?: number;
  /** true en la primera fila del grupo: agrega borde superior para separar grupos */
  readonly isGroupStart?: boolean;
}

export const VersionTableRow: React.FC<VersionTableRowProps> = ({
  version,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  showVersionCell = true,
  versionRowSpan = 1,
  isGroupStart = true
}) => {
  const truncateDescription = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <tr className={`hover:bg-slate-50 transition-colors ${isGroupStart ? 'border-t border-slate-200' : ''}`}>
      <td className="px-6 py-4 align-top">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(version.id)}
          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
        />
      </td>
      {showVersionCell && (
        <td rowSpan={versionRowSpan} className="px-6 py-4 text-sm font-bold text-slate-900 align-top border-l border-slate-100">
          {version.version_code}
        </td>
      )}
      <td className="px-6 py-4 align-top">
        <div className="flex flex-wrap gap-1">
          <VersionTypeBadge tipo={version.tipo} />
          <VersionAreaBadge area={version.area} />
          {version.es_ruptura && <RupturaBadge />}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-700 max-w-md align-top">
        {truncateDescription(version.descripcion)}
      </td>
      <td className="px-6 py-4 text-sm text-slate-600 align-top">
        {formatDateOnly(version.fecha)}
      </td>
      <td className="px-6 py-4 text-right align-top">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(version)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Editar"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(version)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};
